// src/pages/PromotionDiscountHistory.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import "./PromotionDiscountHistory.css";
import { API_BASE } from "../api/config";

export default function PromotionDiscountHistory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const promoIdFromUrl = searchParams.get("promoId") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [shopFilter, setShopFilter] = useState("ALL");
  const [discountTypeFilter, setDiscountTypeFilter] = useState("ALL");

  const normalize = (value) => String(value || "").trim().toUpperCase();

  const formatMoney = (value) => {
    return `${Number(value || 0).toLocaleString()} Ks`;
  };

  const formatDateTime = (value, fallbackDate = "") => {
    if (!value) return fallbackDate || "-";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return fallbackDate || value;

    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPaymentDisplay = (row) => {
    if (row.payment) return row.payment;

    if (Array.isArray(row.splitPayments) && row.splitPayments.length > 0) {
      return row.splitPayments
        .map((p) => {
          const type = p.type || p.method || "Payment";
          const amount = Number(p.amount || 0).toLocaleString();
          return `${type} ${amount} Ks`;
        })
        .join(", ");
    }

    return "-";
  };

  const getDiscountText = (type, value) => {
    const t = normalize(type);

    if (t === "PERCENT") return `${Number(value || 0)}%`;
    if (t === "AMOUNT") return formatMoney(value);
    if (t === "FIXED_BUNDLE_PRICE") return `${formatMoney(value)} bundle total`;
    if (t === "FREE") return "Free";

    return "-";
  };

  const getRuleText = (row) => {
    const type = normalize(row.discountType);
    const value = Number(row.discountValue || 0);

    if (type === "PERCENT") {
      return `${value}% off`;
    }

    if (type === "AMOUNT") {
      return `${formatMoney(value)} off`;
    }

    if (type === "FIXED_BUNDLE_PRICE") {
      return `${formatMoney(value)} bundle total`;
    }

    if (type === "FREE") {
      return "Free";
    }

    return "-";
  };

  useEffect(() => {
    const loadHistory = async () => {
      if (!promoIdFromUrl) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const res = await fetch(
          `${API_BASE}/api/promotion-discount-history/${encodeURIComponent(
            promoIdFromUrl,
          )}`,
        );

        const json = await res.json().catch(() => ({}));

        if (!res.ok || !json?.success) {
          console.error("Promotion discount history fetch failed:", json);
          setRows([]);
          return;
        }

        setRows(json.rows || []);
      } catch (err) {
        console.error("Promotion discount history fetch error:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [promoIdFromUrl]);

  const shops = useMemo(() => {
    const list = rows.map((r) => normalize(r.shop)).filter(Boolean);
    return ["ALL", ...Array.from(new Set(list))];
}, [rows, normalize]);

  const filteredRows = useMemo(() => {
    const q = normalize(search);

    return rows.filter((row) => {
      if (shopFilter !== "ALL" && normalize(row.shop) !== shopFilter) {
        return false;
      }

      if (
        discountTypeFilter !== "ALL" &&
        normalize(row.discountType) !== discountTypeFilter
      ) {
        return false;
      }

      if (!q) return true;

      const text = [
        row.shop,
        row.voucherNo,
        row.saleId,
        row.promoId,
        row.promoName,
        row.itemCode,
        row.color,
        row.size,
        row.customerType,
        getPaymentDisplay(row),
      ]
        .join(" ")
        .toUpperCase();

      return text.includes(q);
    });
  }, [rows, search, shopFilter, discountTypeFilter, normalize, getPaymentDisplay]);

  const summary = useMemo(() => {
    const voucherSet = new Set(filteredRows.map((r) => r.voucherNo));

    const totalDiscount = filteredRows.reduce((sum, r) => {
      return sum + Number(r.discountAmount || 0);
    }, 0);

    const totalDiscountedItems = filteredRows.reduce((sum, r) => {
      return sum + Number(r.qty || 1);
    }, 0);

    const voucherTotalMap = new Map();

    filteredRows.forEach((r) => {
      if (!voucherTotalMap.has(r.voucherNo)) {
        voucherTotalMap.set(r.voucherNo, Number(r.voucherTotal || 0));
      }
    });

    const totalSalesAmount = Array.from(voucherTotalMap.values()).reduce(
      (sum, amount) => sum + amount,
      0,
    );

    return {
      voucherCount: voucherSet.size,
      totalDiscount,
      totalDiscountedItems,
      totalSalesAmount,
    };
  }, [filteredRows]);

  return (
    <Layout title="Instant Discount History">
      <div className="discount-history-page">
        <div className="discount-history-header">
          <div>
            <h2>
              Instant Discount History
              {promoIdFromUrl ? ` - ${promoIdFromUrl}` : ""}
            </h2>
            <p>
              Promotion discount သုံးထားတဲ့ voucher တွေ၊ ဘယ် item ကို
              ဘယ်လောက်လျှော့ထားလဲ ကြည့်ရန်။
            </p>
          </div>

          <button
            type="button"
            className="discount-back-btn"
            onClick={() => navigate("/erp/promotions")}
          >
            Back to Promotions
          </button>
        </div>

        <div className="discount-summary-grid">
          <div className="discount-summary-card">
            <span>Used Vouchers</span>
            <strong>{summary.voucherCount}</strong>
          </div>

          <div className="discount-summary-card">
            <span>Total Discount</span>
            <strong>{formatMoney(summary.totalDiscount)}</strong>
          </div>

          <div className="discount-summary-card">
            <span>Discounted Items</span>
            <strong>{summary.totalDiscountedItems}</strong>
          </div>

          <div className="discount-summary-card">
            <span>Total Sales Amount</span>
            <strong>{formatMoney(summary.totalSalesAmount)}</strong>
          </div>
        </div>

        <div className="discount-filter-card">
          <input
            className="discount-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search voucher / item / shop / promotion..."
          />

          <select
            value={shopFilter}
            onChange={(e) => setShopFilter(e.target.value)}
          >
            {shops.map((shop) => (
              <option key={shop} value={shop}>
                Shop: {shop}
              </option>
            ))}
          </select>

          <select
            value={discountTypeFilter}
            onChange={(e) => setDiscountTypeFilter(e.target.value)}
          >
            <option value="ALL">All Discount Types</option>
            <option value="PERCENT">Percent</option>
            <option value="AMOUNT">Amount</option>
            <option value="FREE">Free</option>
          </select>
        </div>

        <div className="discount-table-wrap">
          <table className="discount-table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Shop</th>
                <th>Voucher No</th>
                <th>Promotion</th>
                <th>Discounted Item</th>
                <th>Original Price</th>
                <th>Discount Type</th>
                <th>Discount Amount</th>
                <th>Final Amount</th>
                <th>Voucher Total</th>
                <th>Customer</th>
                <th>Payment</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="discount-empty">
                    Loading history...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="12" className="discount-empty">
                    No instant discount history found
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.dateTime, row.dateOnly)}</td>

                    <td>
                      <span className="discount-badge shop">
                        {row.shop || "-"}
                      </span>
                    </td>

                    <td className="discount-voucher">
                      {row.voucherNo || "-"}
                    </td>

                    <td>
                      <div className="discount-promo-name">
                        {row.promoName || "-"}
                      </div>
                      <div className="discount-promo-id">
                        {row.promoId || "-"}
                      </div>
                    </td>

                    <td>
                      <div className="discount-item-code">
                        {row.itemCode || "-"}
                      </div>
                      <div className="discount-item-sub">
                        Color: {row.color || "-"} / Size: {row.size || "-"} /
                        Qty: {row.qty || 1}
                      </div>
                    </td>

                    <td>{formatMoney(row.originalPrice)}</td>

                    <td>
                      <span className="discount-status">
                        {getDiscountText(row.discountType, row.discountValue)}
                      </span>
                      <div className="discount-item-sub">
                        {getRuleText(row)}
                      </div>
                    </td>

                    <td className="discount-red">
                      {formatMoney(row.discountAmount)}
                    </td>

                    <td>{formatMoney(row.finalAmount)}</td>

                    <td>{formatMoney(row.voucherTotal)}</td>

                    <td>{row.customerType || "-"}</td>

                    <td>{getPaymentDisplay(row)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}